import loadable from '@loadable/component';
import { useEffect, useState, type FunctionComponent } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { connect } from 'react-unistore';

import * as route from 'src/constants/routes';
import { initDatadogRUM } from 'src/modules/Datadog';
import { useConfig } from 'src/modules/appConfig';
import { useAccountInfo } from 'src/modules/dataManager';
import { getCountryFromSite } from 'src/siteConfig';
import type { ExtractApiActionTypes } from 'src/types';
import { AuthenticatedPageRoute } from 'src/views/shared/AuthenticatedPageRoute/AuthenticatedPageRoute';

import { actions } from './actions';

type Actions = ExtractApiActionTypes<typeof actions>;

// ============================================================================
// code splitting by route
// ============================================================================
const LoadablePayableInvoicesPage = loadable(
  () =>
    import(
      /* webpackChunkName: "payable-page"*/ 'src/views/pages/PayableInvoicesPage'
    ),
);
const LoadableVirtualAccountPage = loadable(
  () =>
    import(
      /* webpackChunkName: "virtual-account-page"*/ 'src/views/pages/VirtualAccountPage'
    ),
);
const LoadablePayInvoicesPage = loadable(
  () =>
    import(/* webpackChunkName: "pay-page"*/ 'src/views/pages/PayInvoicesPage'),
);
const LoadableInvoiceHistoryPage = loadable(
  () =>
    import(
      /* webpackChunkName: "history-page"*/ 'src/views/pages/InvoiceHistoryPage'
    ),
);

export const WrappedRoot = ({
  setAdvertiserInfo,
  setIsAdministrator,
  setIsLoading,
}: Actions) => {
  const { branch, environment, site, version, urlResolver } = useConfig();
  const { advertiser, user, loading, error, authenticated } = useAccountInfo();
  const [isEffectLoading, setIsEffectLoading] = useState(true);

  useEffect(() => {
    if (advertiser || error) {
      const dataDogContext = {
        hirerAccountId: advertiser?.seekId || 'unknown',
        country: getCountryFromSite(site),
      };
      initDatadogRUM(environment, version, branch, dataDogContext);
    }
    setIsAdministrator({
      isAdministrator: advertiser?.isAdministrator || false,
    });
    setIsLoading({ isLoading: loading });

    if (authenticated && advertiser) {
      setAdvertiserInfo({
        advertiserId: advertiser?.advertiserId || '',
        seekAdvertiserId: advertiser?.seekId || '',
        seekBillingAccountId: advertiser?.billingId || '',
        userEmail: user?.email || '',
        adCentreUserId: user?.id || '',
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
      });
    }

    setIsEffectLoading(false);
  }, [
    authenticated,
    advertiser,
    error,
    branch,
    environment,
    loading,
    site,
    user,
    version,
    setAdvertiserInfo,
    setIsAdministrator,
    setIsLoading,
  ]);

  if (isEffectLoading) {
    return <></>;
  }

  return (
    <Routes>
      <Route
        path={urlResolver({ path: route.ROUTE_ROOT })}
        element={<Navigate to={urlResolver({ path: route.ROUTE_PAYABLE })} />}
      />
      <Route
        path={urlResolver({ path: route.ROUTE_PAYABLE })}
        element={
          <AuthenticatedPageRoute>
            <LoadablePayableInvoicesPage />
          </AuthenticatedPageRoute>
        }
      />
      <Route
        path={urlResolver({ path: route.ROUTE_HISTORY })}
        element={
          <AuthenticatedPageRoute>
            <LoadableInvoiceHistoryPage />
          </AuthenticatedPageRoute>
        }
      />
      <Route
        path={urlResolver({ path: route.ROUTE_PAY })}
        element={<LoadablePayInvoicesPage />}
      />
      <Route
        path={urlResolver({ path: route.ROUTE_VIRTUAL_ACCOUNTS })}
        element={<LoadableVirtualAccountPage />}
      />
      <Route
        path={urlResolver({ path: route.ROUTE_PAY_LEGACY })}
        element={
          // @TODO: Make 301 in Melways
          <Navigate to={urlResolver({ path: route.ROUTE_PAY })} />
        }
      />
    </Routes>
  );
};

const Root = connect(
  undefined,
  // @ts-ignore
  actions,
)(WrappedRoot) as unknown as FunctionComponent;

export { Root };
